import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactLoading from 'react-loading';

import EmptyPage from '../../components/empty-page/emptyPage';
import BackIcon from '../../assets/icons/trvto_ArrowBack.svg';

import { supOrgTripDetailsFun } from '../../api/supportAdmin';
import errorHandle from '../../utils/errorHandle';

import {
    BedDouble,
    Building2,
    CalendarDays,
    CheckCircle2,
    ChevronRight,
    House,
    MapPin,
    Maximize,
    ShieldCheck,
    Star,
    Users,
    Wifi
} from 'lucide-react';

import './supOrgAccommodationDetails.css';
import DetailsImageDisplay from '../../components/detailsImageDisplay/DetailsImageDisplay';
/*
 * The tab list (id/label pairs) + tab-bar markup that used to live here
 * are now shared with the customer-facing camp details page - see
 * AccommodationTabBar.jsx for why and the CampDetails.jsx change for
 * the other side of it. `Arrow` (the scroll-arrow icon) moved there
 * with it, so it's no longer imported directly in this file.
 */
import AccommodationTabBar from '../../components/accommodationTabBar/AccommodationTabBar';
/*
 * The 9 tab-content sections (Overview, Units, Amenities, ...) used to be
 * defined locally in this file. They're now shared with the customer-facing
 * camp details page - see AccommodationSections.jsx - so both sides render
 * the exact same content for each tab. The formatting helpers they (and
 * this page's own header/gallery code below) rely on moved with them into
 * accommodationFormatters.js for the same reason.
 */
import AccommodationSectionContent from '../../components/accommodationSections/AccommodationSections';
import { formatLabel, getCurrencySymbol, getImageUrl, getPriceUnit } from '../../components/accommodationSections/accommodationFormatters';

/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function SupOrgAccommodationDetails() {
    const navigate = useNavigate();

    const { orgId, tripId, mode } = useParams();

    const [packData, setPackData] = useState(null);
    const [loading, setLoading] = useState(true);

    const [activeTab, setActiveTab] = useState('basic');

    const [activeImage, setActiveImage] = useState(0);

    /*
     * The tab-scroll-arrow ref/state/effect that used to live here
     * moved into AccommodationTabBar.jsx along with the tab bar markup
     * itself - this page now just owns which tab is active.
     */

    /* =====================================================
       LOAD DATA
    ===================================================== */

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }, []);

    useEffect(() => {
        if (!tripId) {
            setLoading(false);
            return;
        }

        let cancelled = false;

        const loadAccommodation = async () => {
            setLoading(true);

            try {
                const result = await supOrgTripDetailsFun(tripId, 'camp');

                if (!cancelled) {
                    setPackData(result?.data || null);
                }
            } catch (error) {
                if (!cancelled) {
                    errorHandle(error);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadAccommodation();

        return () => {
            cancelled = true;
        };
    }, [tripId]);

    /* =====================================================
       GALLERY
    ===================================================== */

    const galleryImages = useMemo(() => {
        if (!packData) return [];

        const result = [];

        /*
         * Main property media
         */
        if (Array.isArray(packData.media)) {
            packData.media.forEach((item) => {
                /*
                 * Don't put videos into image carousel
                 */
                if (item?.type && item.type !== 'image') {
                    return;
                }

                const url = getImageUrl(item);

                if (url) {
                    result.push(url);
                }
            });
        }

        /*
         * Card images
         */
        if (Array.isArray(packData.cardImages)) {
            packData.cardImages.forEach((item) => {
                const url = getImageUrl(item);

                if (url) {
                    result.push(url);
                }
            });
        }

        /*
         * Unit images
         */
        if (Array.isArray(packData.units)) {
            packData.units.forEach((unit) => {
                if (!Array.isArray(unit?.images)) {
                    return;
                }

                unit.images.forEach((item) => {
                    const url = getImageUrl(item);

                    if (url) {
                        result.push(url);
                    }
                });
            });
        }

        /*
         * Remove duplicate images
         */
        return [...new Set(result)];
    }, [packData]);

    /* =====================================================
       PRICING
    ===================================================== */

    const firstPricing = packData?.units?.[0]?.pricing?.[0];

    const startingPrice = firstPricing?.actualPrice ?? firstPricing?.basePrice ?? null;

    const currencySymbol = getCurrencySymbol(firstPricing?.currency);

    const priceUnitLabel = getPriceUnit(firstPricing?.priceUnit);

    /* =====================================================
       RATING
    ===================================================== */

    const ratingAverage = Number(packData?.rating?.average) || 0;

    const ratingCount = Number(packData?.rating?.count) || 0;

    /* =====================================================
       TOTAL INVENTORY
    ===================================================== */

    const totalUnits = useMemo(() => {
        if (!packData) return 0;

        if (packData.totalUnits !== undefined && packData.totalUnits !== null) {
            return packData.totalUnits;
        }

        return (packData.units || []).reduce((total, unit) => total + Number(unit?.inventory?.totalUnits || 0), 0);
    }, [packData]);

    /* =====================================================
       LOCATION
    ===================================================== */

    const location = [packData?.address?.city, packData?.address?.state].filter(Boolean).join(', ');

    /* =====================================================
       AMENITIES
    ===================================================== */

    const amenities = packData?.amenities || {};

    const allAmenities = Object.values(amenities)
        .flat()
        .filter((item) => typeof item === 'string' && item.trim());

    const uniqueAmenities = [...new Set(allAmenities)];

    const visibleAmenities = uniqueAmenities.slice(0, 6);

    /* =====================================================
       IMAGE NAVIGATION
    ===================================================== */

    const showNextImage = () => {
        if (!galleryImages.length) return;

        setActiveImage((current) => (current >= galleryImages.length - 1 ? 0 : current + 1));
    };

    const showPreviousImage = () => {
        if (!galleryImages.length) return;

        setActiveImage((current) => (current <= 0 ? galleryImages.length - 1 : current - 1));
    };

    /* =====================================================
       BACK
    ===================================================== */

    const handleBack = () => {
        navigate(`/support/${mode}/packages/${orgId}`);
    };

    /* =====================================================
       LOADING
    ===================================================== */

    if (loading) {
        return (
            <div className='supAccDetails-loading'>
                <ReactLoading type='spin' color='#006aff' className='loadingSpin' />
            </div>
        );
    }

    /* =====================================================
       EMPTY
    ===================================================== */

    if (!packData) {
        return (
            <div className='supAccDetails-page'>
                <button className='supAccDetails-backBtn' onClick={handleBack}>
                    <img src={BackIcon} alt='back' />
                    Back to Packages
                </button>

                <EmptyPage />
            </div>
        );
    }

    /* =====================================================
       RENDER
    ===================================================== */

    return (
        <div className='supAccDetails-page'>
            {/* =================================================
                BACK
            ================================================= */}

            <button className='backBtn' style={{ marginLeft: '-20px' }} onClick={handleBack}>
                <img src={BackIcon} alt='back' /> Back to Packages
            </button>

            {/* =================================================
                HEADER
            ================================================= */}

            {/* =================================================
                MAIN TWO COLUMN LAYOUT
            ================================================= */}

            <section className='supAccDetails-layout'>
                {/* =================================================
                    LEFT
                ================================================= */}

                <div className='supAccDetails-left'>
                    <section className='supAccDetails-header'>
                        <div className='supAccDetails-headerMain'>
                            <div className='supAccDetails-titleArea'>
                                <h1 className='detailsImageDisplayName'>{packData.title || 'Accommodation'}</h1>

                                <div className='supAccDetails-meta'>
                                    <span className='tripDetailsSubDetails tripDetailsOrgName'>
                                        {packData?.organizerName ?? 'N/A'}
                                    </span>
                                    {location && (
                                        <span>
                                            <MapPin size={16} />

                                            {location}
                                        </span>
                                    )}

                                    {packData.propertyType && (
                                        <span>
                                            <Building2 size={16} />

                                            {formatLabel(packData.propertyType)}
                                        </span>
                                    )}

                                    {packData.propertyCategory && <span>{formatLabel(packData.propertyCategory)}</span>}

                                    <span className={`supAccDetails-status status-${packData.status}`}>
                                        {formatLabel(packData.status)}
                                    </span>

                                    <span className='supAccDetails-rating'>
                                        <Star size={17} fill='currentColor' />

                                        {ratingAverage > 0 ? ratingAverage.toFixed(1) : 'New'}

                                        {ratingCount > 0 && ` (${ratingCount} reviews)`}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>
                    {/* =============================================
                        IMAGE
                    ============================================= */}

                    <div className='supAccDetails-gallery'>
                        {galleryImages.length > 0 ? (
                            <DetailsImageDisplay imageData={galleryImages} />
                        ) : (
                            <div className='supAccDetails-noImage'>No accommodation images available</div>
                        )}
                    </div>

                    {/* =================================================
    ACCOMMODATION TABS
================================================= */}

                    <AccommodationTabBar activeTab={activeTab} onChange={setActiveTab} sticky />

                    {/* =============================================
                        TAB CONTENT
                    ============================================= */}

                    <div className='supAccDetails-tabContent'>
                        <AccommodationSectionContent activeTab={activeTab} packData={packData} />
                    </div>
                </div>

                {/* =================================================
                    RIGHT
                ================================================= */}

                <aside className='supAccDetails-right'>
                    <div className='supAccDetails-sideCard'>
                        {/* PRICE */}

                        <div className='supAccDetails-priceCard'>
                            <div className='supAccDetails-priceBadges'>
                                {firstPricing?.strikePrice > firstPricing?.actualPrice && (
                                    <span className='supAccDetails-oldPrice'>Rs.{firstPricing.strikePrice}</span>
                                )}

                                {firstPricing?.discountPercent > 0 && (
                                    <span className='supAccDetails-discountBadge'>
                                        {Math.round(firstPricing.discountPercent)}%OFF
                                    </span>
                                )}
                            </div>

                            <div className='supAccDetails-priceRow'>
                                <strong>
                                    {currencySymbol}
                                    {Number(startingPrice || 0).toLocaleString('en-IN')}
                                </strong>

                                <span>/{priceUnitLabel}</span>
                            </div>

                            <div className='supAccDetails-taxText'>Excluding all taxes</div>
                        </div>

                        <div className='supAccDetails-divider' />

                        {/* PROPERTY OVERVIEW */}

                        <div className='supAccDetails-summary'>
                            <h3>Property Overview</h3>

                            <div className='supAccDetails-summaryGrid'>
                                <div>
                                    <BedDouble size={20} />

                                    <div>
                                        <strong>{packData.units?.length || 0}</strong>

                                        <span>Unit types</span>
                                    </div>
                                </div>

                                <div>
                                    <House size={20} />

                                    <div>
                                        <strong>{totalUnits}</strong>

                                        <span>Total units</span>
                                    </div>
                                </div>

                                <div>
                                    <Users size={20} />

                                    <div>
                                        <strong>{packData.maxGuests || '-'}</strong>

                                        <span>Max guests</span>
                                    </div>
                                </div>

                                <div>
                                    <Maximize size={20} />

                                    <div>
                                        <strong>{packData.totalBedrooms || 0}</strong>

                                        <span>Bedrooms</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* CHECK IN / OUT */}

                        <div className='supAccDetails-stayInfo'>
                            <div>
                                <CalendarDays size={19} />

                                <div>
                                    <span>Check-in</span>

                                    <strong>{packData.policies?.checkInTime || 'Not specified'}</strong>
                                </div>
                            </div>

                            <div>
                                <CalendarDays size={19} />

                                <div>
                                    <span>Check-out</span>

                                    <strong>{packData.policies?.checkOutTime || 'Not specified'}</strong>
                                </div>
                            </div>
                        </div>

                        {/* AMENITIES */}

                        {visibleAmenities.length > 0 && (
                            <div className='supAccDetails-quickAmenities'>
                                <h3>Popular amenities</h3>

                                {visibleAmenities.map((amenity, index) => (
                                    <div key={`${amenity}-${index}`}>
                                        <CheckCircle2 size={16} />

                                        <span>{amenity}</span>
                                    </div>
                                ))}

                                {uniqueAmenities.length > 6 && (
                                    <button type='button' onClick={() => setActiveTab('amenities')}>
                                        View all amenities
                                        <ChevronRight size={15} />
                                    </button>
                                )}
                            </div>
                        )}

                        {/* ORGANIZER */}

                        <div className='supAccDetails-organizerCard'>
                            <span>Organizer</span>

                            <strong>{packData.organizerName || 'N/A'}</strong>
                        </div>

                        {/* LOCATION */}

                        <div className='supAccDetails-locationCard'>
                            <MapPin size={19} />

                            <div>
                                <span>Location</span>

                                <strong>{location || 'N/A'}</strong>
                            </div>
                        </div>

                        {/* TRUST */}

                        <div className='supAccDetails-trust'>
                            <div>
                                <ShieldCheck size={20} />

                                <span>Verified accommodation</span>
                            </div>

                            <div>
                                <Wifi size={20} />

                                <span>TRVTO listing</span>
                            </div>
                        </div>
                    </div>
                </aside>
            </section>
        </div>
    );
}

/*
 * The 9 tab-content section components (OverviewSection, UnitsSection,
 * AmenitiesSection, PricingSection, ServicesSection, PoliciesSection,
 * MediaSection, LocationSection, AdditionalSection) that used to be
 * defined below this point now live in
 * ../../components/accommodationSections/AccommodationSections.jsx,
 * shared with the customer-facing CampDetails.jsx via the
 * AccommodationSectionContent switch imported above, so both sides
 * render identical tab content. Audience-based visibility (what shows
 * for user vs. support vs. org) is left as a future filter inside that
 * shared switch, not implemented here.
 */
